import { UseQueryOptions } from "@tanstack/react-query";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { GetUserProfileData } from "@/types/userProfile";

import { useDeepCompareMemo } from "use-deep-compare";

// In case there are updates on returned API, can be changed centrally here
export enum EModuleName {
  SPATIAL_ANALYSIS = "spatial-analysis",
  DATASET_EXPLORER = "data-explorer",
  DATASET_MANAGEMENT = "user-dataset-management",
  USER_MANAGEMENT = "user-management",
  USER_GROUP_MANAGEMENT = "user-group-permission"
}

// In case there are updates on returned API, can be changed centrally here
export enum EModulePermission {
  VIEW = "view",
  EDIT = "edit",
  DELETE = "delete",
  CREATE = "create"
}

type PermissionMapping = {
  [EModulePermission.VIEW]: boolean;
  [EModulePermission.EDIT]: boolean;
  [EModulePermission.DELETE]: boolean;
  [EModulePermission.CREATE]: boolean;
};

export const DEFAULT_USER_PERMISSION_MAPPING: PermissionMapping = {
  [EModulePermission.VIEW]: false,
  [EModulePermission.EDIT]: false,
  [EModulePermission.DELETE]: false,
  [EModulePermission.CREATE]: false
};

const flattenPermissions = (
  modulePermissions: string[]
): Record<EModuleName, PermissionMapping> =>
  modulePermissions.reduce<Record<EModuleName, PermissionMapping>>(
    (acc, current) => {
      const [moduleName, modulePermission] = current.split(":") as [
        EModuleName,
        EModulePermission
      ];
      if (!acc[moduleName]) {
        acc[moduleName] = { ...DEFAULT_USER_PERMISSION_MAPPING };
      }
      acc[moduleName][modulePermission] = true;
      return acc;
    },
    {
      [EModuleName.SPATIAL_ANALYSIS]: { ...DEFAULT_USER_PERMISSION_MAPPING },
      [EModuleName.DATASET_EXPLORER]: { ...DEFAULT_USER_PERMISSION_MAPPING },
      [EModuleName.DATASET_MANAGEMENT]: { ...DEFAULT_USER_PERMISSION_MAPPING },
      [EModuleName.USER_MANAGEMENT]: { ...DEFAULT_USER_PERMISSION_MAPPING },
      [EModuleName.USER_GROUP_MANAGEMENT]: {
        ...DEFAULT_USER_PERMISSION_MAPPING
      }
    }
  );

/**
 * Custom hook to get user permissions.
 * The user permissions are fetched from the user profile API
 * and processed to return a structured object.
 *
 * The usage on code can be like this:
 * ```typescript
 * const userPermissions = useGetUserPermission();
 * const isCreateUserGranted =
 *   userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.CREATE];
 * ```
 * @returns {Object} - Object containing user permissions
 */
export const useGetUserPermission = (
  options?: Omit<
    UseQueryOptions<GetUserProfileData, Error, GetUserProfileData>,
    "queryKey" | "queryFn"
  >
) => {
  const { data: myProfile } = useGetUserProfileAPI(options);

  return useDeepCompareMemo(
    () => flattenPermissions(myProfile?.features || []),
    [myProfile?.features]
  );
};
